'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef } from 'react'

import { TKeywordMarker } from '@/entities/map'
import { NoSearchResult } from '@/entities/room'
import {
  getLocationName,
  MobileLocationHistoryList,
  MobileRecentRoomList,
  type History,
} from '@/features/room-search'
import { Badge, Highlight, Icon, SegmentedControl, Spinner } from '@/shared/ui'

import * as Location from './location'
import { useKeywordAutocomplete } from '../lib/keyword-autocomplete'
import { TabType } from '../lib/tab'

type Props = {
  keyword: string
  tab: TabType
  onTabChange: (value: string) => void
  onHistoryClick: (history: History) => void
  onLocationSelect: (location: TKeywordMarker) => void
}

export const OverlayContent = ({
  keyword,
  tab,
  onTabChange,
  onHistoryClick,
  onLocationSelect,
}: Props) => {
  const t = useTranslations()

  const TAB_LABELS: Record<TabType, string> = useMemo(
    () => ({
      [TabType.ALL]: t('map_search.location_picker.all.title'),
      [TabType.REGION]: t('map_search.location_picker.region.title'),
      [TabType.SUBWAY]: t(
        'map_search.location_picker.subway_station.tab_title',
      ),
      [TabType.LANDMARK]: t('map_search.location_picker.place.title'),
    }),
    [t],
  )

  const {
    loading,
    regions,
    subways,
    landmarks,
    hasNoMarker,
    hasRegions,
    hasSubways,
    hasLandmarks,
  } = useKeywordAutocomplete(keyword)

  const onTabChangeRef = useRef(onTabChange)

  onTabChangeRef.current = onTabChange

  const isFirstRenderRef = useRef(true)

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false

      return
    }
    onTabChangeRef.current(TabType.ALL)
  }, [regions, subways, landmarks])

  const handleLocationClick = (location: TKeywordMarker) => () => {
    onLocationSelect(location)
  }

  if (keyword === '') {
    return (
      <>
        <MobileLocationHistoryList
          onItemClick={onHistoryClick}
          className="not-last:pb-6"
        />
        <MobileRecentRoomList className="not-first:pt-6" />
      </>
    )
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (hasNoMarker) {
    return (
      <div className="flex h-fit w-full flex-col items-center justify-center gap-4 pt-19 md:pt-30">
        <NoSearchResult />
        <h3 className="typo-headline2 font-bold text-black">
          {t('map_search.location_picker.empty.title')}
        </h3>
        <p className="typo-body1 text-center whitespace-break-spaces text-gray-900">
          {t('map_search.location_picker.empty.description')}
        </p>
      </div>
    )
  }

  const tabVisibility: Record<TabType, boolean> = {
    [TabType.ALL]: true,
    [TabType.REGION]: hasRegions,
    [TabType.SUBWAY]: hasSubways,
    [TabType.LANDMARK]: hasLandmarks,
  }

  const visibleTabs = (Object.keys(TAB_LABELS) as TabType[]).filter(
    tabKey => tabVisibility[tabKey],
  )

  const showRegions = tab === TabType.ALL ? hasRegions : tab === TabType.REGION
  const showSubways = tab === TabType.ALL ? hasSubways : tab === TabType.SUBWAY
  const showLandmarks =
    tab === TabType.ALL ? hasLandmarks : tab === TabType.LANDMARK

  return (
    <>
      <div className="mx-auto w-[290px]">
        <SegmentedControl.Root
          type="single"
          shape="pill"
          size="small"
          variant="padded"
          width="full"
          value={tab as string}
          onValueChange={onTabChange}
          className="mb-3"
        >
          {visibleTabs.map(tabKey => (
            <SegmentedControl.Item key={tabKey} value={tabKey as string}>
              {TAB_LABELS[tabKey]}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>
      </div>

      <div className="space-y-3">
        {showRegions && (
          <Location.List title={TAB_LABELS.region}>
            {regions.map(region => (
              <Location.Item
                key={`${region.lat}-${region.lng}-${region.name}`}
                onClick={handleLocationClick(region)}
              >
                <Highlight
                  query={keyword}
                  className="typo-body2 inline truncate font-normal text-black"
                >
                  {getLocationName(region)}
                </Highlight>
              </Location.Item>
            ))}
          </Location.List>
        )}

        {showSubways && (
          <Location.List
            title={t('map_search.location_picker.subway_station.title')}
          >
            {subways.map(subway => (
              <Location.Item
                key={`${subway.lat}-${subway.lng}-${subway.name}`}
                onClick={handleLocationClick(subway)}
                trailing={
                  <ul className="flex shrink-0 flex-wrap items-center gap-1">
                    {subway.subwayLines.map(line => (
                      <li key={line.name}>
                        <Badge variant="solid" color={line.color}>
                          {line.displayName}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                }
              >
                <Highlight
                  query={keyword}
                  className="typo-body2 inline truncate font-normal text-black"
                >
                  {subway.name}
                </Highlight>
              </Location.Item>
            ))}
          </Location.List>
        )}

        {showLandmarks && (
          <Location.List title={TAB_LABELS.landmark}>
            {landmarks.map(landmark => (
              <Location.Item
                key={`${landmark.lat}-${landmark.lng}-${landmark.name}`}
                onClick={handleLocationClick(landmark)}
                trailing={
                  <Icon
                    name="flag"
                    size={18}
                    className="shrink-0 text-blue-500"
                  />
                }
              >
                <Highlight
                  query={keyword}
                  className="typo-body2 inline truncate font-normal text-black"
                >
                  {landmark.name}
                </Highlight>
              </Location.Item>
            ))}
          </Location.List>
        )}
      </div>
    </>
  )
}
